// frontend/src/pages/CorpLanding.tsx
// 法人専用リンク /corp/:code の入口。会社コードはURLで確定し、社員は社員番号のみ入力する。
// 未ログイン時は LINE / WeChat / メールで登録・ログイン(InviteLanding と同方式)。
// ログイン後は sessionStorage の pending_corp_code を見て AppRoutes が本ページへ復帰させる。
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLine, FaWeixin, FaEnvelope, FaBuilding, FaIdBadge } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import EmailAuthModal from '../components/EmailAuthModal';

const CorpLanding: React.FC = () => {
  const { t } = useTranslation();
  const { code: rawCode } = useParams<{ code: string }>();
  const code = (rawCode || '').trim().toUpperCase();
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  const [empNo, setEmpNo] = useState('');
  const [birth, setBirth] = useState('');   // 生年月日(第二認証因子)
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const codeValid = /^[A-Z0-9_-]+$/.test(code);

  // 会社コードを保存(ログイン往復・OAuth を跨いで本ページへ復帰させるため)。妥当なコードのみ、
  // 時刻付き(古い保留は AppRoutes 側で期限切れ扱いにする)。
  useEffect(() => {
    if (code && codeValid) {
      sessionStorage.setItem('pending_corp_code', code);
      sessionStorage.setItem('pending_corp_code_at', String(Date.now()));
    }
  }, [code, codeValid]);

  const clearPending = () => {
    try {
      sessionStorage.removeItem('pending_corp_code');
      sessionStorage.removeItem('pending_corp_code_at');
    } catch { /* noop */ }
  };

  // 社員番号で在籍確認(会社コードは URL 確定)
  const handleVerify = async () => {
    const no = empNo.trim();
    if (!no) { toast.error(t('corp.enter_employee_no', '社員番号を入力してください')); return; }
    if (!birth.trim()) { toast.error(t('corp.enter_birth', '生年月日を入力してください')); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/company/verify', { company_code: code, employee_no: no, birth_date: birth });
      clearPending();
      toast.success(`${res.data?.company_name || ''} ${t('points.verify_success', '社員認証が完了しました')}`.trim());
      navigate('/mypage/points', { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || t('corp.verify_failed', '社員認証に失敗しました');
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // LINE ログイン(ref なし。ログイン後は本ページへ戻り、社員番号入力へ)
  const handleLineLogin = async () => {
    const liffId = process.env.REACT_APP_LINE_LIFF_ID;
    if (!liffId) { toast.error('LINE LIFF ID is not configured'); return; }
    setLoading(true);
    try {
      const liff = (await import('@line/liff')).default;
      await liff.init({ liffId });
      if (!liff.isLoggedIn()) { liff.login(); return; }
      const idToken = liff.getIDToken();
      if (!idToken) throw new Error('Failed to get ID Token');
      const res = await api.post('/auth/line', { id_token: idToken });
      if (res.data.access_token) {
        login(res.data.access_token, res.data.role || 'user', 'line');
        navigate(`/corp/${code}`, { replace: true });
      }
    } catch (err) {
      console.error('LINE Login error:', err);
      toast.error(t('auth.error_failed') || 'ログインに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // WeChat ログイン(戻り先は AppRoutes の pending_corp_code 復帰で本ページへ)
  const handleWechatLogin = () => {
    const isWechatBrowser = /micromessenger/i.test(navigator.userAgent);
    window.location.href = isWechatBrowser ? '/api/wechat-mp/login' : '/api/wechat/login';
  };

  // LIFF 経由でリダイレクト後、既にログイン済みなら自動処理
  useEffect(() => {
    const liffId = process.env.REACT_APP_LINE_LIFF_ID;
    if (liffId && !isAuthenticated) {
      import('@line/liff').then(({ default: liff }) => {
        liff.init({ liffId }).then(() => { if (liff.isLoggedIn()) handleLineLogin(); }).catch(() => {});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 max-w-sm w-full border border-white/20 text-center"
        >
          <div className="w-20 h-20 bg-coral/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaBuilding className="text-coral text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">{t('corp.title', '社員福利厚生のご登録')}</h2>
          <p className="text-gray-500 text-xs mb-1">{t('corp.company_code_label', '会社コード')}: <span className="font-mono font-bold text-gray-700">{code}</span></p>

          {isAuthenticated ? (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                {t('corp.enter_employee_no_desc', '勤務先から配布された社員番号と生年月日を入力してください。')}
              </p>
              <div className="text-left">
                <label className="text-xs text-gray-500 mb-1 flex items-center gap-1"><FaIdBadge /> {t('points.verify_employee_no', '社員番号')}</label>
                <input
                  value={empNo}
                  onChange={(e) => setEmpNo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
                  placeholder={t('corp.employee_no_placeholder', '例: 12345')}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-coral/40"
                />
                <label className="text-xs text-gray-500 mb-1 block">{t('corp.birth_date', '生年月日')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={birth}
                  onChange={(e) => setBirth(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
                  placeholder={t('corp.birth_placeholder', '例: 19900115 または 1990-01-15')}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-coral/40"
                />
              </div>
              <button
                onClick={handleVerify}
                disabled={submitting || !empNo.trim() || !birth.trim()}
                className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all ${submitting || !empNo.trim() || !birth.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-coral hover:opacity-90 active:scale-[0.98]'}`}
              >
                {submitting ? t('common.loading', '処理中...') : t('corp.verify_submit', '社員認証する')}
              </button>
              <button onClick={() => { clearPending(); navigate('/mypage', { replace: true }); }} className="w-full text-gray-400 text-sm hover:text-gray-600 transition pt-3">
                {t('common.later', '後で')}
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                {t('corp.login_desc', 'まずはログイン(または新規登録)してください。')}
              </p>
              <div className="space-y-3">
                <button onClick={handleLineLogin} disabled={loading}
                  className="w-full bg-[#06C755] hover:bg-[#05b14c] text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-500/20 disabled:opacity-60">
                  <FaLine className="text-2xl" />{loading ? t('common.loading') : t('auth.login_line', 'LINEでログイン')}
                </button>
                <button onClick={handleWechatLogin} disabled={loading}
                  className="w-full bg-[#07C160] hover:bg-[#06a854] text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-400/20 disabled:opacity-60">
                  <FaWeixin className="text-2xl" />{t('auth.login_wechat', 'WeChatでログイン')}
                </button>
                <button onClick={() => setShowEmailModal(true)} disabled={loading}
                  className="w-full bg-coral hover:opacity-90 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-coral/20 disabled:opacity-60">
                  <FaEnvelope className="text-xl" />{t('invite.register_email', 'メールで登録')}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <EmailAuthModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} redirectTo={`/corp/${code}`} />
    </div>
  );
};

export default CorpLanding;
